import { useState, useCallback } from "react";
import { ToolContainer, FileDropZone } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { extractFeatures, compareFeatures, isIdentical, describeScore, fmtSeconds, fmtOffset, type Comparison } from "./compare";
import { sniffSampleRate } from "./sniffRate";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { CompareViewPanel, type ViewSide } from "./CompareViewPanel";
import { measureLoudness } from "./compareView";
import { ToolInfoSection } from "@/components/ToolInfoSection";

const TOOL_INFO_CONTENT = {
  what: `Compares two audio files and shows how much of the sound matches, as a percentage, and where they differ — useful for checking whether two recordings are the same take, whether a recording was edited, or whether two files are duplicates despite different names or formats. Runs entirely in your browser — neither file is uploaded anywhere.`,
  how: `Upload two audio files and click Compare. Each is turned into a voice-and-sound fingerprint over time, leading and trailing silence is ignored, the two are lined up by start offset (shown as, for example, aligned by +0.50 s), and the matching parts are scored with loudness evened out. A strip shows second by second where file 1 matches file 2. Below it you can see both waveforms and their difference, play either file and flip between them at the same moment (with loudness matched), read a table of duration, loudness and peak, and save the difference as a WAV.`,
  tip: `Being upfront about what this measures: it compares how the sound changes over time (the pattern of voices, notes and instruments), not exact bytes, and not a commercial fingerprint like Shazam. Volume, format, quality and steady background noise matter little. Different recordings of the same song or the same spoken text can score lower than identical copies. It checks the first 4 minutes of each file, and it is not forensic-grade.`,
};

interface Decoded { buffer: AudioBuffer; lufs: number | null; peakDb: number; mono: Float32Array; sampleRate: number; fileRate: number | null; ext: string; seconds: number; channels: number; }

async function decode(file: File): Promise<Decoded> {
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  const ctx = new AudioCtx();
  try {
    const bytes = await file.arrayBuffer();
    const fileRate = sniffSampleRate(new Uint8Array(bytes, 0, Math.min(bytes.byteLength, 1 << 16)));
    const buf = await ctx.decodeAudioData(bytes);
    const mono = new Float32Array(buf.length);
    for (let c = 0; c < buf.numberOfChannels; c++) {
      const data = buf.getChannelData(c);
      for (let i = 0; i < data.length; i++) mono[i] += data[i] / buf.numberOfChannels;
    }
    const chans = Array.from({ length: buf.numberOfChannels }, (_, c) => buf.getChannelData(c));
    const lev = measureLoudness(chans, buf.sampleRate);
    return { buffer: buf, lufs: lev.lufs, peakDb: lev.peakDb, mono, sampleRate: buf.sampleRate, fileRate, ext: (file.name.split(".").pop() || "").toLowerCase(), seconds: buf.duration, channels: buf.numberOfChannels };
  } finally { ctx.close().catch(() => {}); }
}

interface Result { comparison: Comparison; identical: boolean; encodingDiffers: boolean; a: Decoded; b: Decoded; }

export default function CompareAudio() {
  const [fileA, setFileA] = useState<File | null>(null);
  const [fileB, setFileB] = useState<File | null>(null);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const { logUsageNow } = useLogUsageOnAction();

  const handleFilesA = useCallback((files: File[]) => { const f = files[0]; if (f) { setFileA(f); setResult(null); } }, []);
  const handleFilesB = useCallback((files: File[]) => { const f = files[0]; if (f) { setFileB(f); setResult(null); } }, []);

  const compare = async () => {
    if (!fileA || !fileB) return;
    setComparing(true);
    setResult(null);
    try {
      const [a, b] = await Promise.all([decode(fileA), decode(fileB)]);
      // Let the "Analyzing..." state paint before the (synchronous) number crunching.
      await new Promise((r) => setTimeout(r, 30));
      const identical = isIdentical(a.mono, b.mono);
      const comparison = identical
        ? { score: 100, raw: 1, strip: [], cells: [], differences: [], differingSeconds: 0, analysedSeconds: a.seconds, offsetSeconds: 0, mode: "shape" } as Comparison
        : compareFeatures(extractFeatures(a.mono, a.sampleRate), extractFeatures(b.mono, b.sampleRate));
      const encodingDiffers = a.ext !== b.ext || (a.fileRate !== null && b.fileRate !== null && a.fileRate !== b.fileRate);
      // Choosing files costs nothing; the trial use is taken when the result is shown (once per visit, so comparing again is free).
      if (!(await logUsageNow())) return;
      setResult({ comparison, identical, encodingDiffers, a, b });
      toast.success("Comparison complete!");
    } catch (err) {
      console.error("Audio comparison failed:", err);
      toast.error("Couldn't analyze one or both files — make sure they're valid, playable audio files.");
    } finally {
      setComparing(false);
    }
  };

  const reset = () => { setFileA(null); setFileB(null); setResult(null); };
  const facts = (d: Decoded, f: File) => `${fmtSeconds(d.seconds)} · ${d.fileRate ? `${Math.round(d.fileRate / 100) / 10} kHz · ` : ""}${d.channels === 1 ? "mono" : d.channels === 2 ? "stereo" : `${d.channels} channels`} · ${(f.size / 1024 / 1024).toFixed(1)} MB`;

  const viewSide = (d: Decoded, f: File | null): ViewSide => ({
    buffer: d.buffer, mono: d.mono,
    facts: { name: f?.name ?? "", seconds: d.seconds, fileRate: d.fileRate, decodedRate: d.sampleRate, channels: d.channels, bytes: f?.size ?? 0, lufs: d.lufs, peakDb: d.peakDb },
  });
  const headline = result ? describeScore(result.comparison.score, result.comparison.differences, result.identical, result.encodingDiffers, { differingSeconds: result.comparison.differingSeconds, offsetSeconds: result.comparison.offsetSeconds }) : "";

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm font-medium mb-2">Audio File 1</p>
          {fileA ? (
            <div className="p-4 rounded-lg border bg-card space-y-2">
              <p className="text-sm truncate">{fileA.name}</p>
              {result && <p className="text-xs text-muted-foreground">{facts(result.a, fileA)}</p>}
              <Button variant="outline" size="sm" onClick={() => { setFileA(null); setResult(null); }}>Change</Button>
            </div>
          ) : (
            <FileDropZone accept="audio/*" gateAtDownload onFiles={handleFilesA} label="Drop first audio file" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Audio File 2</p>
          {fileB ? (
            <div className="p-4 rounded-lg border bg-card space-y-2">
              <p className="text-sm truncate">{fileB.name}</p>
              {result && <p className="text-xs text-muted-foreground">{facts(result.b, fileB)}</p>}
              <Button variant="outline" size="sm" onClick={() => { setFileB(null); setResult(null); }}>Change</Button>
            </div>
          ) : (
            <FileDropZone accept="audio/*" gateAtDownload onFiles={handleFilesB} label="Drop second audio file" />
          )}
        </div>
      </div>

      <div className="mt-6 text-center space-y-4">
        <div className="flex justify-center gap-3">
          <Button onClick={compare} disabled={!fileA || !fileB || comparing} size="lg">
            {comparing ? "Analyzing..." : "Compare Audio"}
          </Button>
          {result && <Button variant="outline" size="lg" onClick={reset}>Compare another pair</Button>}
        </div>

        {result && (
          <div className="p-6 rounded-xl border bg-primary/5 max-w-3xl mx-auto text-left space-y-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Match</p>
              <p className="text-4xl font-bold text-primary" data-testid="compare-score">{result.comparison.score.toFixed(1)}%</p>
              <p className="text-sm text-muted-foreground mt-1" data-testid="compare-label">{describeScore(result.comparison.score, result.comparison.differences, result.identical, result.encodingDiffers, { differingSeconds: result.comparison.differingSeconds, offsetSeconds: result.comparison.offsetSeconds })}</p>
              {fmtOffset(result.comparison.offsetSeconds) && !result.identical && (
                <p className="text-sm text-muted-foreground" data-testid="compare-offset">{fmtOffset(result.comparison.offsetSeconds)} (file 2's sound starts {result.comparison.offsetSeconds > 0 ? "later" : "earlier"} than file 1's)</p>
              )}
            </div>
            {!result.identical && result.comparison.cells.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Each cell is 1 second of file 1 (green matches, red differs, grey is silence)</p>
                <div className="flex h-6 gap-px rounded overflow-hidden" data-testid="compare-strip">
                  {result.comparison.cells.map((c, i) => (
                    <div key={i} className="flex-1 min-w-[2px]" title={`${fmtSeconds(i)}–${fmtSeconds(i + 1)}: ${c === "silence" ? "silence" : c === "match" ? "matches" : "differs"}`}
                      style={{ background: c === "silence" ? "hsl(0 0% 60% / 0.25)" : c === "match" ? "hsl(145 60% 42%)" : "hsl(0 70% 50%)" }} />
                  ))}
                </div>
                {result.comparison.differences.length > 0 && (
                  <p className="text-sm mt-2" data-testid="compare-diffs">
                    Differs at {result.comparison.differences.map(([a, b]) => `${fmtSeconds(a)}–${fmtSeconds(b)}`).join(", ")} in file 1.
                  </p>
                )}
              </div>
            )}
            <CompareViewPanel
              a={viewSide(result.a, fileA)} b={viewSide(result.b, fileB)}
              offsetSeconds={result.identical ? 0 : result.comparison.offsetSeconds}
              cells={result.comparison.cells} identical={result.identical}
              headline={headline} scorePct={result.comparison.score}
              offsetLine={!result.identical && fmtOffset(result.comparison.offsetSeconds) ? `${fmtOffset(result.comparison.offsetSeconds)} (file 2's sound starts ${result.comparison.offsetSeconds > 0 ? "later" : "earlier"} than file 1's)` : ""}
              differsLine={result.comparison.differences.length ? `Differs at ${result.comparison.differences.map(([x, y]) => `${fmtSeconds(x)}–${fmtSeconds(y)}`).join(", ")} in file 1.` : ""}
            />
            {result.a.seconds > 240 || result.b.seconds > 240 ? (
              <p className="text-xs text-muted-foreground">Only the first 4 minutes of each file were compared.</p>
            ) : null}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
